import React, { memo, useState } from "react";
import { Box } from "@mui/material";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  SUCCESS,
  WARNING,
  TEAL,
  ORANGE,
  INDIGO,
  NAVY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import CustomModal from "../../../../../components/Modals/CustomModal";
import {
  sharedBillingStyles as s,
  formatDate,
  formatCredits,
  FEATURE_KEY_LABELS,
} from "./billingUtils";
import BillingBuyCreditsCard from "./BillingBuyCreditsCard";

const BillingOverviewSection = memo(
  ({
    billingPlan,
    creditUsage,
    invoices,
    setActiveTab,
    isSdr: isSdrProp,
    topUps,
    buyingPlanId,
    handleBuyPackage,
  }) => {
    const [allInvoicesModalOpen, setAllInvoicesModalOpen] = useState(false);
    // $0 trial-record invoices (Stripe creates one at every trial start,
    // "no payment required") are bookkeeping, not bills — hide them from
    // the user-facing lists so a trial tenant doesn't see a confusing $0 row.
    const visibleInvoices = (invoices || []).filter(
      (inv) =>
        // drafts aren't customer documents yet (no number, not charged) — Stripe
        // finalizes them ~1h after creation and they appear then, properly numbered
        inv?.status !== "draft" &&
        (Number(inv?.amount_due) > 0 ||
          Number(inv?.amount_paid) > 0 ||
          // a heavily-discounted (or below-Stripe-minimum) PAID invoice can have
          // amount_paid 0 with a real total — that's a genuine bill, show it
          Number(inv?.total) > 0),
    );
    const plan = billingPlan?.plan;
    const hasPlan = billingPlan?.has_plan;
    // Cancellation is SCHEDULED (Stripe keeps status "active"/"trialing" until period end) —
    // the overview must reflect it: badge, no "next billing" (nothing will be billed), and
    // the renewal banner flips to a reactivate prompt.
    const isCancelScheduled =
      !!plan?.cancel_at_period_end && plan?.status !== "canceled";
    // First post-trial hour: Stripe already says "active" but the cycle invoice isn't
    // PAID yet — show an honest transitional badge instead of a fully-paid "Active".
    const isPaymentPending = plan?.payment_pending === true && !isCancelScheduled;
    const credits = creditUsage;
    // isSdr from prop (useBillingData), plan data, or credit usage API
    const isSdr =
      isSdrProp === true ||
      plan?.isSdr === true ||
      plan?.isSdr === "true" ||
      credits?.isSdr === true ||
      credits?.isSdr === "true";
    const isUnlimited =
      isSdr || credits?.total_credits === -1 || plan?.total_credits === -1;
    const creditUsedPct =
      isUnlimited || !credits?.total_credits
        ? 0
        : (credits.credits_used / credits.total_credits) * 100;
    // "Other" reconciler: any used credits not attributed to the 3 tracked features, so the breakdown
    // always sums to the headline "used" (covers untracked/edge debits). Usually 0 (all debits map to
    // reveal_details / icp / personalisation).
    const trackedUsageSum = credits?.usage_by_feature
      ? ["reveal_details", "icp", "personalisation"].reduce(
          (a, k) => a + (credits.usage_by_feature[k] || 0),
          0,
        )
      : 0;
    const otherUsage = Math.max(0, (credits?.credits_used || 0) - trackedUsageSum);

    const featureColors = [
      BRAND.PRIMARY,
      ORANGE.W_500,
      TEAL.W_500,
      INDIGO.W_500,
      NAVY.W_500,
    ];

    const renderInvoiceDownload = (inv, idx) => {
      const invoiceLabel = inv?.number || `Invoice ${idx + 1}`;
      // A PAID invoice must open the Stripe HOSTED page (green "Paid" state + receipt),
      // not the PDF - the PDF is a snapshot from finalization (a second before the
      // auto-charge) and forever says "Amount due / Pay online" even after payment.
      const invoiceUrl =
        inv?.status === "paid" && inv?.hosted_invoice_url
          ? inv.hosted_invoice_url
          : inv?.invoice_pdf || inv?.hosted_invoice_url;
      if (!invoiceUrl) {
        return (
          <Box
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "30px",
              height: "30px",
              borderRadius: "7px",
              border: `1px solid ${BORDER.DEFAULT}`,
              background: BASE.BACKGROUND_LIGHT,
              opacity: 0.6,
            }}
          >
            <IconComponent
              iconLabel={ICON_LABELS.CLOUD_DOWNLOAD}
              style={{
                fontSize: "17px",
                color: GREY.TERTIARY,
              }}
            />
          </Box>
        );
      }

      return (
        <Box
          component="a"
          href={invoiceUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Download invoice ${invoiceLabel}`}
          style={{
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            borderRadius: "7px",
            border: `1px solid ${BORDER.DEFAULT}`,
            background: BASE.WHITE,
            transition: "border-color 0.18s ease, background 0.18s ease",
          }}
          sx={{
            "&:hover": {
              borderColor: BORDER.HOVER,
              background: BASE.BACKGROUND_LIGHT,
            },
            "&:focus-visible": {
              outline: `2px solid ${BRAND.PRIMARY}`,
              outlineOffset: "2px",
            },
          }}
        >
          <IconComponent
            iconLabel={ICON_LABELS.CLOUD_DOWNLOAD}
            style={{
              cursor: "pointer",
              fontSize: "17px",
              color: GREY.TERTIARY,
            }}
          />
        </Box>
      );
    };

    return (
      <Box style={s.contentWrapper}>
        {/* Renewal banner */}
        {hasPlan && billingPlan?.renewal_message && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "16px 20px",
              background: isCancelScheduled ? WARNING.W_50 : SUCCESS.W_50,
              borderRadius: "10px",
              border: `1px solid ${
                isCancelScheduled ? WARNING.W_200 : SUCCESS.W_200
              }`,
            }}
          >
            <IconComponent
              iconLabel={ICON_LABELS.INFO}
              style={{
                color: isCancelScheduled ? WARNING.W_600 : SUCCESS.W_600,
                fontSize: "20px",
              }}
            />
            <Box style={{ flex: 1 }}>
              <TypographyInline
                size="sm"
                color={GREY.PRIMARY}
                fontWeight={fontWeight.MEDIUM}
                label={
                  isCancelScheduled
                    ? `${billingPlan.renewal_message} Your subscription is cancelled — you keep full access until then.`
                    : `${billingPlan.renewal_message}${
                        !isSdr && plan?.billing_period === "month"
                          ? " Save 20% by switching to annual billing."
                          : ""
                      }`
                }
              />
            </Box>
            <Box style={{ width: "fit-content" }}>
              <Button
                label={isCancelScheduled ? "Reactivate" : "Upgrade Now"}
                size="sm36"
                variant="primary"
                onClick={() =>
                  setActiveTab(isCancelScheduled ? "settings" : "plans")
                }
              />
            </Box>
          </Box>
        )}

        {/* Plan + Credits row */}
        <Box
          style={{
            display: "grid",
            gap: "24px",
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1fr) minmax(0, 2fr)",
            },
          }}
        >
          {/* Current Plan */}
          <Box style={s.card}>
            <Box style={s.cardHeader}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Current Plan"
              />
              <span
                style={{
                  ...s.badge,
                  background:
                    !isCancelScheduled && !isPaymentPending && plan?.status === "active"
                      ? SUCCESS.W_50
                      : WARNING.W_50,
                  color:
                    !isCancelScheduled && !isPaymentPending && plan?.status === "active"
                      ? SUCCESS.W_600
                      : WARNING.W_600,
                }}
              >
                {isCancelScheduled
                  ? `Cancels on ${formatDate(plan?.next_billing_date)}`
                  : isPaymentPending
                    ? "Activating · payment processing"
                    : plan?.status === "trialing"
                      ? "Free Trial"
                      : plan?.status
                        ? plan.status.charAt(0).toUpperCase() +
                          plan.status.slice(1)
                        : "No Plan"}
              </span>
            </Box>

            <Box
              style={{ display: "flex", alignItems: "baseline", gap: "4px" }}
            >
              <TypographyInline
                size="dxs"
                fontWeight={fontWeight.BOLD}
                color={BRAND.PRIMARY}
                label={plan?.amount ? `$${plan.amount}` : "$0"}
              />
              <TypographyInline
                size="sm"
                color={GREY.SECONDARY}
                label={plan?.billing_period === "month" ? "/mo" : "/yr"}
              />
            </Box>
            <TypographyInline
              size="sm"
              color={GREY.SECONDARY}
              label={`${plan?.name || "Free"} · ${
                plan?.billing_period === "month" ? "Monthly" : "Annual"
              }`}
            />

            <Box
              style={{
                display: "grid",
                gap: "10px",
                marginTop: "16px",
              }}
              sx={{
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
              }}
            >
              <Box>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={isCancelScheduled ? "ACTIVE UNTIL" : "NEXT BILLING"}
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={formatDate(plan?.next_billing_date)}
                />
                {isCancelScheduled && (
                  <TypographyInline
                    size="xs"
                    color={GREY.TERTIARY}
                    label="You won't be billed again."
                  />
                )}
              </Box>
              {/* <Box>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="ACCOUNT SEATS"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label={`${billingPlan?.account_seats?.used || 0} / ${
                    billingPlan?.account_seats?.total == null
                      ? "Unlimited"
                      : billingPlan.account_seats.total
                  }`}
                />
              </Box> */}
            </Box>

            <Box style={{ marginTop: "18px", width: "fit-content" }}>
              <Button
                label="Upgrade Subscription"
                size="sm36"
                variant="ghost"
                onClick={() => setActiveTab("plans")}
              />
            </Box>
          </Box>

          {/* Credit Usage */}
          <Box style={s.card}>
            {/* <Box style={s.cardHeader}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Credit Usage"
              />
              <TypographyInline
                size="xs"
                color={GREY.SECONDARY}
                label={`Resets on ${formatDate(credits?.reset_date)}`}
              />
            </Box> */}

            <Box
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              {isUnlimited ? (
                <TypographyInline
                  size="dxs"
                  fontWeight={fontWeight.BOLD}
                  color={BRAND.PRIMARY}
                  label="Unlimited Credits"
                />
              ) : (
                <>
                  <TypographyInline
                    size="dxs"
                    fontWeight={fontWeight.BOLD}
                    color={GREY.PRIMARY}
                    label={formatCredits(credits?.credits_used)}
                  />
                  <TypographyInline
                    size="sm"
                    color={GREY.SECONDARY}
                    label={`/ ${formatCredits(credits?.total_credits)} credits used`}
                  />
                </>
              )}
            </Box>

            {/* Progress bar - hidden for unlimited plans */}
            {!isUnlimited && (
              <Box style={{ marginTop: "12px" }}>
                <Box style={s.progressTrack}>
                  <Box
                    style={{
                      ...s.progressFill,
                      width: `${Math.min(creditUsedPct, 100)}%`,
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Plan + top-up split — shown when top-ups were bought this cycle, so a total that
                exceeds the plan allotment (and the "used" that counts them) is self-explanatory. */}
            {!isUnlimited && credits?.topup_credits > 0 && (
              <Box
                style={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label={`Plan ${formatCredits(credits?.plan_credits)}`}
                />
                <TypographyInline size="xs" color={GREY.TERTIARY} label="·" />
                <TypographyInline
                  size="xs"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={SUCCESS.W_600}
                  label={`Top-up +${formatCredits(credits?.topup_credits)}`}
                />
              </Box>
            )}

            <Box
              style={{
                display: "grid",
                gap: "10px",
                marginTop: "14px",
              }}
              sx={{
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <Box style={s.statBox}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="REMAINING"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.BOLD}
                  color={SUCCESS.W_600}
                  label={
                    isUnlimited
                      ? "Unlimited"
                      : formatCredits(credits?.remaining_credits)
                  }
                />
              </Box>
              <Box style={s.statBox}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  fontWeight={fontWeight.MEDIUM}
                  label="DAILY AVG"
                />
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.BOLD}
                  color={GREY.PRIMARY}
                  label={`${credits?.daily_average || 0} cr`}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Buy More Credits - hidden for unlimited / SDR plans */}
        {!isUnlimited && (
          <BillingBuyCreditsCard
            remaining={credits?.remaining_credits}
            topUps={topUps}
            buyingPlanId={buyingPlanId}
            onBuy={handleBuyPackage}
          />
        )}

        {/* Usage breakdown + Invoices */}
        <Box
          style={{
            display: "grid",
            gap: "24px",
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              lg: isSdr ? "1fr" : "1fr 1fr",
            },
          }}
        >
          {/* Usage by Feature - hidden for SDR plans */}
          {!isSdr && (
            <Box style={s.card}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Usage by Feature"
              />
              <Box
                style={{
                  marginTop: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {credits?.usage_by_feature &&
                  Object.entries(credits.usage_by_feature)
                    .filter(
                      ([key]) =>
                        !["sequence_builder", "template"].includes(key),
                    )
                    .map(([key, val], idx) => (
                      <Box key={idx}>
                        <Box
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <TypographyInline
                            size="sm"
                            color={GREY.PRIMARY}
                            label={FEATURE_KEY_LABELS[key] || key}
                          />
                          <TypographyInline
                            size="xs"
                            fontWeight={fontWeight.SEMIBOLD}
                            color={GREY.PRIMARY}
                            label={`${formatCredits(val)} cr`}
                          />
                        </Box>
                        <Box
                          style={{
                            height: "9px",
                            background: BORDER.DISABLED,
                            border: `1px solid ${BORDER.LIGHT}`,
                            borderRadius: "999px",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            style={{
                              width: `${Math.min(
                                ((val || 0) / (credits?.total_credits || 1)) *
                                  100,
                                100,
                              )}%`,
                              height: "100%",
                              background:
                                featureColors[idx % featureColors.length],
                              borderRadius: "999px",
                              transition:
                                "width 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                {/* Other = used credits not attributed to a tracked feature, so the
                    breakdown always reconciles to the headline "used". Usually hidden. */}
                {otherUsage > 0 && (
                  <Box>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "6px",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        color={GREY.PRIMARY}
                        label="Other"
                      />
                      <TypographyInline
                        size="xs"
                        fontWeight={fontWeight.SEMIBOLD}
                        color={GREY.PRIMARY}
                        label={`${formatCredits(otherUsage)} cr`}
                      />
                    </Box>
                    <Box
                      style={{
                        height: "9px",
                        background: BORDER.DISABLED,
                        border: `1px solid ${BORDER.LIGHT}`,
                        borderRadius: "999px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        style={{
                          width: `${Math.min(
                            (otherUsage / (credits?.total_credits || 1)) * 100,
                            100,
                          )}%`,
                          height: "100%",
                          background:
                            featureColors[featureColors.length - 1],
                          borderRadius: "999px",
                          transition:
                            "width 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Recent Invoices */}
          <Box style={s.card}>
            <TypographyInline
              size="md"
              fontWeight={fontWeight.SEMIBOLD}
              color={GREY.PRIMARY}
              label="Recent Invoices"
            />
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              {visibleInvoices.length > 0 ? (
                visibleInvoices.slice(0, 3).map((inv, idx) => (
                  <Box
                    key={idx}
                    style={s.invoiceRow}
                    sx={{
                      "&:hover": {
                        borderColor: BORDER.HOVER,
                        background: BASE.WHITE,
                        boxShadow: `0 10px 20px -26px ${GREY.SECONDARY}`,
                      },
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.SEMIBOLD}
                        color={GREY.PRIMARY}
                        label={inv.number}
                      />
                      <TypographyInline
                        size="xs"
                        color={GREY.SECONDARY}
                        label={formatDate(inv.created)}
                      />
                    </Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.SEMIBOLD}
                        color={GREY.PRIMARY}
                        label={`${inv.currency?.toUpperCase()} ${inv.amount_paid}`}
                      />
                      {renderInvoiceDownload(inv, idx)}
                    </Box>
                  </Box>
                ))
              ) : (
                <TypographyInline
                  size="sm"
                  color={GREY.TERTIARY}
                  label="No invoices found."
                />
              )}
            </Box>
            <Box style={{ marginTop: "18px", width: "fit-content" }}>
              <Button
                label="View All Invoices"
                size="sm36"
                variant="ghost"
                onClick={() => setAllInvoicesModalOpen(true)}
              />
            </Box>
          </Box>
        </Box>

        <CustomModal
          open={allInvoicesModalOpen}
          close={() => setAllInvoicesModalOpen(false)}
          onClose={() => setAllInvoicesModalOpen(false)}
          header={{
            heading: {
              id: "all_invoices",
              label: "All Invoices",
            },
            description: {
              id: "all_invoices_desc",
              label: `Showing ${visibleInvoices.length} invoices`,
            },
          }}
          width="760px"
          footer={{
            right: (
              <Box style={{ width: "fit-content" }}>
                <Button
                  label="Close"
                  variant="ghost"
                  size="sm36"
                  onClick={() => setAllInvoicesModalOpen(false)}
                />
              </Box>
            ),
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {visibleInvoices.length > 0 ? (
              <Box
                style={{
                  maxHeight: "56vh",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  paddingRight: "2px",
                }}
              >
                {visibleInvoices.map((inv, idx) => (
                  <Box
                    key={inv?.number || idx}
                    style={s.invoiceRow}
                    sx={{
                      "&:hover": {
                        borderColor: BORDER.HOVER,
                        background: BASE.WHITE,
                        boxShadow: `0 10px 20px -26px ${GREY.SECONDARY}`,
                      },
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.SEMIBOLD}
                        color={GREY.PRIMARY}
                        label={inv?.number || `Invoice #${idx + 1}`}
                      />
                      <TypographyInline
                        size="xs"
                        color={GREY.SECONDARY}
                        label={formatDate(inv?.created)}
                      />
                    </Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.SEMIBOLD}
                        color={GREY.PRIMARY}
                        label={`${inv?.currency?.toUpperCase() || "USD"} ${inv?.amount_paid ?? 0}`}
                      />
                      {renderInvoiceDownload(inv, idx)}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <TypographyInline
                size="sm"
                color={GREY.TERTIARY}
                label="No invoices found."
              />
            )}
          </Box>
        </CustomModal>
      </Box>
    );
  },
);

BillingOverviewSection.displayName = "BillingOverviewSection";

export default BillingOverviewSection;
